import React, { FC, useCallback, useState } from 'react';
import { MyBookingWarehouseCard } from 'features/WarehouseCard';
import { useOpenWarehouseDoorMutation, Warehouse } from 'entities/Warehouse';
import { BoxesList } from 'features/BoxesList';
import { Divider } from 'antd';
import { CachedBox } from 'entities/Box';
import { Reservation } from 'entities/Reservation';
import { ContractWithBookingInfo } from 'entities/Contract';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { MessagePanel } from 'shared/ui/MessagePanel';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { showNotification } from 'app/providers/NotificationsProvider';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { OpenBox } from 'features/OpenBox';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface MyBookingProps {
  type: 'reservation' | 'contract';
  warehouse: Nullable<Warehouse>;
  onOpenContractDetails: (contractId: string) => void;
  boxes?: CachedBox[];
  reservation?: Nullable<Reservation>;
  contractsData?: ContractWithBookingInfo[];
}

export const MyBooking: FC<MyBookingProps> = (props) => {
  const { warehouse, type, boxes, reservation, contractsData, onOpenContractDetails } = props;

  const { t } = useAppTranslation(['common', 'contracts']);

  const { isDesktop } = useScreenBreakpoints();

  const [isModalOpened, setModalOpened] = useState<boolean>(false);

  const [openWarehouseDoor, { isLoading: isOpenLoading }] = useOpenWarehouseDoorMutation();
  const coords = useGeolocation();

  const isReservation = type === 'reservation';

  const handleCloseModal = useCallback((): void => {
    setModalOpened(false);
  }, []);

  const handleOpenModal = useCallback((): void => {
    setModalOpened(true);
  }, []);

  const handleOpenDoor = useCallback(
    async (lockAddress: Nullable<string>): Promise<void> => {
      if (warehouse && lockAddress) {
        try {
          await openWarehouseDoor({
            warehouseId: warehouse.warehouseId,
            args: { lockAddress, longitude: coords?.lng, latitude: coords?.lat },
          }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('The door has been successfully opened', { ns: 'common' }));
        } catch (error: CustomAny) {
          console.log(error);
          const errorMessage = t(error?.data?.message?.[0] || 'Error while opening door', { ns: 'common' });

          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        }
      }
    },
    [coords?.lat, coords?.lng, openWarehouseDoor, t, warehouse],
  );

  return (
    <div className="flex flex-col gap-1 desktop:gap-6">
      <MyBookingWarehouseCard warehouse={warehouse} type={type} />
      {/* <Divider className="m-0" /> */}
      <BoxesList
        boxes={boxes}
        type={type}
        warehouseId={warehouse?.warehouseId}
        reservation={reservation}
        contractsData={contractsData}
        onOpenContractDetails={onOpenContractDetails}
      />
      {!isReservation && !isDesktop && (
        <div className="fixed bottom-0 left-0 w-full z-20 bg-backgroundColor">
          <div className="flex justify-center p-2 px-4">
            <Button containerClassName="w-full" onClick={handleOpenModal} size="medium">
              {t('Open doors')}
            </Button>
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpened} onClose={handleCloseModal} paddingLess width={360} footer={false}>
        <div className="text-2xl p-4 font-tektur">{t('Open', { ns: 'contracts' })}</div>
        <MessagePanel
          className="w-[360px]"
          type="error"
          showWarningAnimation={true}
          title={
            <div className="px-2 text-error text-sm">
              {t('You should connect to the local warehouse wi-fi network or be not far than 500m from warehouse')}
            </div>
          }
          actions={
            <>
              <div className="flex flex-col justify-center gap-2">
                <div className="font-tektur">{t('Warehouse')}</div>
                {warehouse?.internalEquipments?.map((equipment) => (
                  <Button
                    isLoading={isOpenLoading}
                    key={equipment.internalEquipmentId}
                    onClick={() => {
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      handleOpenDoor(equipment.lockAddress);
                    }}
                  >
                    {getLocalizedString(equipment.name)}
                  </Button>
                ))}

                <div className="font-tektur">{t('Box')}</div>
                {boxes?.map((box) => (
                  <OpenBox
                    box={box}
                    key={box.boxId}
                    action={
                      <Button iconPosition="prev" containerClassName="w-full">
                        {t('Open', { ns: 'contracts' })} {box.name}
                      </Button>
                    }
                  />
                ))}

                <Button theme="outlined" onClick={handleCloseModal} isLoading={isOpenLoading} className="mt-4">
                  {t('Cancel')}
                </Button>
              </div>
            </>
          }
        />
      </Modal>
    </div>
  );
};
